import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";

// EVERYTHING READ FROM YOUR FILE (F39 + F93), per document — the owner render of the SAME
// `document_join` projection the staff console reads. This component computes nothing: every label,
// state word, reason, empty line, summary and total arrives from
// `projections/fact_sheet.py`. The only thing decided here is how many rent-roll rows are on screen.
//
// F96: "Used in the mark" is neutral BOLD ink, never green — green is "confirmed" and nothing else.
// Colour is carried by nothing in here; the four states are told apart by words and weight.

const ROWS_SHOWN = 10;

// F110 (founder ruled 30 Sep): a page link mints its capability ON CLICK from the session. The old
// sheet minted one 15-minute token when the FILE was opened, so a click a quarter of an hour later
// opened a 404 and the link looked dead. The href stays real (copyable, right-clickable); the click
// swaps in a fresh one. A mint that fails falls back to the href the sheet arrived with.
//
// F123 (founder 30 Sep): ONE tab per DOCUMENT. Every page click used to open another tab, so eight
// citations left eight copies of the same PDF open. The tab is NAMED after the document, so the next
// click on the same document reuses that tab and moves it to the new page; a different document gets
// its own tab.
export function usePageOpener() {
  const { authedFetch } = useAuth();
  return (fileId, page) => (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
    ev.preventDefault();
    const href = ev.currentTarget.getAttribute("href");
    const name = `juno-doc-${fileId}`;
    const tab = window.open("", name);                         // a handle: the document is same-origin
    const go = (path) => {
      const url = new URL(path, window.location.origin);
      if (page != null) url.hash = `page=${page}`;
      if (tab && !tab.closed) {
        tab.location.href = url.toString();
        tab.focus();
      } else {
        window.open(url.toString(), name);
      }
    };
    authedFetch(`/owner/document-link/${fileId}`, { method: "POST" })
      .then((r) => go(r.view_url))
      // A mint that fails falls back to the href the sheet arrived with; a link with no href (the
      // rationale's citation, which is a button) falls back to the tokenless path, so the tab shows
      // an honest 404 instead of sitting blank.
      .catch(() => go(href || `/api/owner/document/${fileId}`));
  };
}

function Row({ row, fileId, open }) {
  const cls = row.tone === "used" ? " fs-row-used"
    : row.tone === "held" ? " fs-row-held"
    : row.tone === "none" ? " fs-row-none" : "";
  return (
    <div className={"fs-row" + cls} data-testid={"fs-row-" + row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>
      <div className="fs-row-top">
        <span className="fs-k">{row.label}</span>
        <span className="fs-v">{row.value == null ? "—" : row.value}</span>
      </div>
      <div className="fs-meta">
        <span className="fs-state" data-testid="fs-state">{row.state_label}</span>
        {row.page_href
          ? <a className="fs-page" href={row.page_href} target="_blank" rel="noopener noreferrer"
               onClick={open(fileId, row.page)} data-testid="fs-page-link">{row.page_label}</a>
          : row.page_label ? <span className="fs-page-none">{row.page_label}</span> : null}
        {row.worked_out
          ? <span className="fs-page-none" data-testid="fs-worked-out">{row.worked_out}</span>
          : row.no_page && !row.page_label && <span className="fs-page-none">no page found</span>}
      </div>
      {row.note && <span className="fs-note">{row.note}</span>}
    </div>
  );
}

function Cell({ cell, fileId, open }) {
  const cls = cell.tone === "none" ? " fs-rr-cell-none"
    : cell.tone === "held" ? " fs-rr-cell-held" : "";
  return (
    <span className={"fs-rr-cell" + cls}>
      {cell.page_href
        ? <a href={cell.page_href} target="_blank" rel="noopener noreferrer"
             onClick={open(fileId, cell.page)} data-testid="fs-rr-page-link">{cell.value}</a>
        : <span>{cell.value == null ? "—" : cell.value}</span>}
      {cell.tone === "held" && <span className="fs-rr-held">held back · no page</span>}
    </span>
  );
}

function RentRoll({ rows, totals, fileId, open }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, ROWS_SHOWN);
  return (
    <div className="fs-rr" data-testid="fs-rent-roll">
      <div className="fs-rr-head">
        <span className="fs-rr-h">TENANT</span>
        <span className="fs-rr-h fs-rr-h-r">SF</span>
        <span className="fs-rr-h fs-rr-h-r">ANNUAL RENT</span>
      </div>
      {shown.map((t, i) => (
        <div className="fs-rr-row" key={i} data-testid={"fs-rr-row-" + i}>
          <span className="fs-rr-name">{t.tenant_name || "—"}</span>
          <Cell cell={t.sf} fileId={fileId} open={open} />
          <Cell cell={t.annual_rent} fileId={fileId} open={open} />
        </div>
      ))}
      <div className="fs-rr-total" data-testid="fs-rr-total">
        <span>{totals.tenants}</span>
        <span className="fs-rr-cell">
          {totals.sf || "—"}
          {totals.sf_note && <span className="fs-rr-tnote" data-testid="fs-rr-sf-note">{totals.sf_note}</span>}
        </span>
        <span className="fs-rr-cell">{totals.annual_rent || "—"}</span>
      </div>
      {rows.length > ROWS_SHOWN && (
        <button className="fs-rr-more" data-testid="fs-rr-more" onClick={() => setAll((v) => !v)}>
          {all ? `Show the first ${ROWS_SHOWN}` : `Show all ${rows.length} tenants`}
        </button>
      )}
      <span className="fs-note" style={{ paddingTop: 8 }}>
        Click any SF or rent to open it on its page. Nothing in this table sets the mark.
        {totals.page_note && (
          <span className="fs-rr-pagenote" data-testid="fs-rr-page-note">{totals.page_note}</span>
        )}
      </span>
    </div>
  );
}

function Document({ doc }) {
  const open = usePageOpener();
  return (
    <div className="fs-doc" data-testid={"fs-doc-" + doc.file_id}>
      <div className="fs-head">
        <span className="fs-file" data-testid="fs-filename">{doc.filename}</span>
        {doc.view_url && (
          <a className="fs-open" href={doc.view_url} target="_blank" rel="noopener noreferrer"
             onClick={open(doc.file_id, null)} data-testid="fs-open-document">Open the document</a>
        )}
      </div>
      <p className="fs-summary" data-testid="fs-summary">{doc.summary}</p>
      {doc.verdict_line && <p className="fs-verdict" data-testid="fs-verdict">{doc.verdict_line}</p>}
      {doc.sections.map((sec) => (
        <div className="fs-section" key={sec.title}
             data-testid={"fs-section-" + sec.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>
          <span className="fs-section-title">{sec.title}</span>
          {sec.empty
            ? <span className="fs-section-empty" data-testid="fs-section-empty">{sec.empty_line}</span>
            : sec.rows.map((r) => <Row key={r.label} row={r} fileId={doc.file_id} open={open} />)}
          {sec.rent_roll && <RentRoll rows={sec.rent_roll} totals={sec.rent_roll_totals}
                                     fileId={doc.file_id} open={open} />}
        </div>
      ))}
      <div className="fs-section" data-testid="fs-section-also">
        <span className="fs-section-title">ALSO IN THE DOCUMENT</span>
        <p className="fs-lead" data-testid="fs-extras-lead">{doc.extras_lead}</p>
        {doc.extras.map((e, i) => {
          const body = (
            <>
              <span className="fs-extra-label">&ldquo;{e.label}&rdquo;</span>
              <span className="fs-extra-value">{e.value}</span>
              <span className="fs-extra-page">{e.page_label}</span>
            </>
          );
          return e.page_href
            ? <a className="fs-extra" key={i} href={e.page_href} target="_blank" rel="noopener noreferrer"
                 onClick={open(doc.file_id, e.page)} data-testid={"fs-extra-" + i}>{body}</a>
            : <div className="fs-extra" key={i} data-testid={"fs-extra-" + i}>{body}</div>;
        })}
      </div>
      <p className="fs-footer">{doc.footer}</p>
    </div>
  );
}

export function FactSheet({ buildingRef, refreshKey }) {
  const { authedFetch } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    authedFetch(`/owner/building/${buildingRef}/fact-sheet`).then(setSheet)
      .catch((e) => setErr(e.detail || "I couldn't load what I read from your file."));
  }, [buildingRef, authedFetch]);
  useEffect(() => { setSheet(null); setErr(""); load(); }, [load, refreshKey]);

  if (err) return <p className="panel-muted" data-testid="fs-error">{err}</p>;
  if (!sheet) return <p className="panel-muted" data-testid="fs-loading">Reading…</p>;
  if (!sheet.documents.length) {
    return <p className="panel-muted" data-testid="fs-empty">{sheet.empty_reason}</p>;
  }
  return (
    <div className="fs" data-testid="fact-sheet">
      {sheet.documents.map((d) => <Document key={d.file_id} doc={d} />)}
    </div>
  );
}
